import { ModalService } from 'src/app/modals/modal.service';
import {Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewContainerRef} from '@angular/core';
import { ViewEncapsulation } from '@angular/core';
import { HookResult, StateService, TargetState, Transition, TransitionService } from "@uirouter/angular";
import {ViewService} from './services/view.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { BannerService, DateHelpers, EdlAlert, PortalService, SessionService } from '@edl-development/edl-common';
import { environment } from './../environments/environment';
import { interval, startWith, mergeMap, Subscription, Observable } from 'rxjs';
import { multiFactor } from 'firebase/auth';
import { forEach } from 'lodash';

declare let gtag: Function;

@Component({
    selector: 'edl-paetep',
    templateUrl: 'edl-paetep.component.html',
    encapsulation: ViewEncapsulation.None
})

export class EdlPaetepComponent implements OnInit, OnDestroy {
    private subscriptions?: [Subscription];


    constructor(
        private viewService: ViewService,
        private vcr: ViewContainerRef,
        private transitionService: TransitionService,
        private loadingBarService: LoadingBarService,
        private stateService: StateService,
        private portalService: PortalService,
        private bannerService: BannerService,
        public sessionService: SessionService,
        public ModalService: ModalService
    ) {
        viewService.viewContainerRef = vcr;

        this.sessionService.PersistToken.push('user-default-building', 'user_default_schoolYear', 'user-default-role-search', 'user-default-action-required', 'user-default-search-string', 'user-default-supervisor-only', 'user-default-search-string');

        this.sessionService.InitializeAuth(environment.FIREBASECONFIG, environment.FIREBASE_TENANT);

        transitionService.onSuccess({ }, (transition) => {
            try {
                gtag('set', 'page_path', window.location.pathname);
                gtag('event', 'page_view');
            } catch (e){ }
        });
        transitionService.onSuccess({ }, (transition) => {
            if (transition.to()?.name === 'public.logout') {
                this.sessionService.destroy();

                // Need to include any custom options that were passed to the transition
                const custom = transition?.options()?.custom;

                // Redirect to the login page
                this.stateService.go('public.login', {}, { custom });
            } else if(this.sessionService.isAuthenticated() ) {
                this.sessionService.Initialized(true);
            }

            if (transition.to().name !== transition.from().name) {
                window.scrollTo(0, 0);
            }

            this.loadingBarService.stop();

            this.toggleScrollBehavior_Smooth();
        });

        transitionService.onError({}, (transition: Transition) => {
            const to = transition.to();
            const error = transition.error();
            const params = transition.params();

            this.loadingBarService.useRef().stop();

            if(error?.detail?.name == "ChunkLoadError") {
              this.showReloadMessage();
            } else if (error?.detail?.name == "TransitionRejection") {
              this.reloadWindow()
            }

            // If an error occurs due to the user not having permission to access the page
            // then take them to the unauthorized page
            if(error?.detail?.status === 401 || error?.detail?.status === 403) {
                transition.router.stateService.go('public.unauthorized');
            } else if ((error?.detail?.status ?? 0) >= 400) {
                transition.router.stateService.go('public.generic-error');
            }

            this.toggleScrollBehavior_Smooth();
        });


        transitionService.onBefore({}, async (transition) => {
            let from = transition.from();
            let to = transition.to();
            const isPublic = to?.data?.isPublic;

            if (!isPublic && this.sessionService.isAuthenticated() && sessionService.userAgreement && to.name !== 'public.user-agreement') {
              const loggedInPortal = sessionService.PortalSettings?.URLSubdirectory;
              return transition.router.stateService.target('public.user-agreement', { loggedInPortal });
            }
            else if(!isPublic && this.sessionService.mfar && (to.name !== 'authenticated.switch-portals' && to.name !== 'authenticated.users.user.edit' && to.name !== 'public.user-agreement')){
               return await this.checkAuthState(transition);

            }

            if (from.name !== to.name) {
                this.loadingBarService.set(0);
                this.loadingBarService.start();
            }

            this.toggleScrollBehavior_Auto();

            return undefined;
        });


    }

    async checkAuthState(transition:Transition){
        return await  new Promise<HookResult>((resolve, reject) => {
          this.sessionService.GetAuthApp().onAuthStateChanged(authUser => {

            try{
              const loggedInPortal = this.sessionService.PortalSettings?.URLSubdirectory;
              const user = this.sessionService.user;

              //var AuthUser = this.sessionService.GetAuthApp().currentUser!;
              const multiFactorUser = multiFactor(authUser!);
              if(multiFactorUser.enrolledFactors.length === 0 || !user?.SQPresent){
                resolve( transition.router.stateService.target('authenticated.users.user.edit', {loggedInPortal, userId: user?.UserID}));
              }
              resolve(undefined);
            }catch(e){
              resolve(undefined);
            }
          });
        });
    }



    showReloadMessage(){
        this.ModalService.openApplicationReloadModal().then((result) => {
            if(result){
              this.reloadWindow();
            }
        });
    }

    reloadWindow(){
      window.location.replace('https://'+window.location.hostname + '?t=' + new Date().getTime());
    }

    ngOnInit() {

        // Check for maintenance notification every 10 minutes
        let r  = interval(600000)
            .pipe(
                startWith(0),
                mergeMap(() => this.portalService.getAlert())
            ).subscribe((alert?: EdlAlert) => {
                const currentMessage = alert?.AlertMessage;
                const currentSeverity = alert?.AlertSeverity;
                const previousMessage = this.bannerService.getMessage();
                const previousSeverity = this.bannerService.getSeverity();

                const startDate = DateHelpers.shortDateStringToDate(alert?.StartDate);
                const endDate = DateHelpers.shortDateStringToDate(alert?.EndDate);
                const today = DateHelpers.getDateWithoutTime(new Date());

                if ((previousMessage !== currentMessage || previousSeverity !== currentSeverity) &&
                    currentMessage &&
                    currentSeverity &&
                    alert.IsActive &&
                    DateHelpers.isDateBetween(today, startDate, endDate)){
                    this.bannerService.setMessage(currentMessage);
                    this.bannerService.setSeverity(currentSeverity);
                    this.bannerService.open();
                }
            });
        this.subscriptions?.push(r);
    }

    ngOnDestroy() {
      forEach(this.subscriptions, (subscription) => {
        subscription.unsubscribe();
      }
);
    }

    toggleScrollBehavior_Auto() {
        document.documentElement.style.scrollBehavior = 'auto';
    }

    toggleScrollBehavior_Smooth() {
        document.documentElement.style.scrollBehavior = 'smooth';
    }
}
